/**
 * Keeping each firm user's client keys in their account, so signing in
 * unlocks every client they may open:
 *
 * - On sign-in (and on reload, while the vault is open), the client keys
 *   shared with the user are opened with their private key, checked, and
 *   unlocked. A copy that fails the check is dropped so a good one can be
 *   shared in its place.
 * - Whenever this page holds a client's full key (pasted, or restored),
 *   it's sealed to everyone who may open that client and lacks it, but only
 *   to the user themselves and to colleagues whose key the user has
 *   verified (see `pins`): the Hub lists recipients and their keys, and must
 *   not be able to add a key of its own.
 * - A key is only treated as a client's if it's the client's current key
 *   and the client's signing identity (inside the key) signed it. The
 *   identity is pinned the first time it's seen.
 *
 * The Hub only stores sealed keys (see `keyGrants`). Failures are quiet:
 * whatever didn't happen now is retried on the next sign-in or unlock.
 */
import { x25519 } from '@noble/curves/ed25519';
import { useAccountStore } from '~/stores/account';
import { useOrgKeyUnlockStore } from '~/stores/orgKeyUnlock';
import { base64ToBytes, bytesToBase64 } from '~/util/bytes';
import * as hubClient from '~/util/hubClient';
import { sessionStill } from '~/util/sessionEpoch';
import { openSecret, sealSecret, secretMatches } from '~/util/keyGrants';
import { accountFingerprint, activationMessage, engineShare, identityPublicKey, parseOrgSecret, verifySignature, } from '~/util/orgSigning';
import { acceptedActivation, getPins, pinActivation, pinOrgIdentity, pinPerson, pinsFrozen, } from '~/util/pins';
function accountPrivateKey() {
    const stored = useAccountStore().privateKeyBase64;
    return stored ? base64ToBytes(stored) : null;
}
/** The signed-in user's own public key, computed from their private key
 * rather than taken from the Hub. */
function ownPublicKey(privateKey) {
    return bytesToBase64(x25519.getPublicKey(privateKey));
}
/** Activations accepted in this page load, by account and client: the
 * floor even if this browser's storage is blocked. Cleared with the
 * session. */
const heldFloor = new Map();
const floorKey = (orgId) => `${useAccountStore().privateKeyBase64 ?? ''}\u0000${orgId}`;
export function forgetHeldActivations() {
    heldFloor.clear();
}
/** The newest activation this person has accepted for `orgId`. */
function acceptedFloor(orgId, pins) {
    const all = [
        pins.activations?.[orgId],
        acceptedActivation(orgId),
        heldFloor.get(floorKey(orgId)),
    ].filter((a) => !!a);
    return all.reduce((top, a) => (!top || a.seq > top.seq ? a : top), null);
}
/**
 * The organization's current key, if `secret` is it: the encryption key
 * matches, and (for an organization with signing) the identity inside the
 * secret is the organization's, signed this key's activation, and is the
 * identity pinned for the organization (pinned now if first seen, except
 * after a rollback of the pins, when an identity "first seen" may be the one
 * the rollback was for: then nothing is accepted until the person has
 * acknowledged the rollback and verified it). The activation must be no
 * older than the newest this person has accepted for the client, and at the
 * same `seq`, for the same key.
 *
 * Nothing is saved for anyone but the person who asked: a sign-out or
 * another sign-in while it waits ends it, unchecked.
 */
export async function verifyHeldSecret(orgId, secret, seen = {}) {
    const held = useAccountStore().privateKeyBase64;
    const session = sessionStill();
    const stillTheirs = () => session() && useAccountStore().privateKeyBase64 === held;
    const orgKey = await hubClient.getOrgKey(orgId);
    if (!stillTheirs())
        return null;
    seen.otherKey = !!orgKey && !secretMatches(secret, orgKey.public_key);
    if (!orgKey || seen.otherKey)
        return null;
    const { identitySeed } = parseOrgSecret(secret);
    const pins = await getPins();
    if (!stillTheirs())
        return null;
    const pinned = pins.orgIdentities[orgId];
    if (!identitySeed) {
        // A key from before signing: only for an organization that never had an
        // identity. A client this browser has an identity pinned for is never
        // downgraded to an unsigned key, whatever the Hub says about it now, and
        // neither is any client while a rollback of the pins is unacknowledged.
        seen.outdated = !!orgKey.identity_public_key;
        if (pinned || pinsFrozen())
            return null;
        return orgKey.identity_public_key ? null : orgKey;
    }
    const identity = identityPublicKey(identitySeed);
    if (orgKey.identity_public_key !== identity)
        return null;
    if (orgKey.seq === undefined || !orgKey.signature)
        return null;
    const message = activationMessage(orgId, orgKey.seq, orgKey.key_version, orgKey.public_key);
    if (!verifySignature(identity, message, orgKey.signature))
        return null;
    if (pinned && pinned !== identity)
        return null;
    // Never an older activation than this person has accepted for the client
    // (in any of their browsers): a Hub that replays a rotated-away key (and
    // its genuine, older activation) would otherwise have commands sealed to a
    // key someone who left may hold.
    const floor = acceptedFloor(orgId, pins);
    if (floor && orgKey.seq < floor.seq)
        return null;
    if (floor && orgKey.seq === floor.seq && floor.key !== orgKey.public_key)
        return null;
    if (!pinned) {
        if (pinsFrozen())
            return null;
        // Pinned in this browser at once; the save to the Hub goes on without
        // holding up the unlock (a Hub that never answers can't stall it).
        pinOrgIdentity(orgId, identity).catch(() => undefined);
    }
    // Raised only once the key has passed every check, so a key refused here
    // never moves the floor.
    if (!floor || orgKey.seq > floor.seq) {
        heldFloor.set(floorKey(orgId), { seq: orgKey.seq, key: orgKey.public_key });
        pinActivation(orgId, orgKey.seq, orgKey.public_key).catch(() => undefined);
    }
    return orgKey;
}
const BEHIND = "The Hub's record of this client's key is older than one you've already accepted (it may have been restored from a backup, or changed). Nothing was signed. Tell DELT4 before changing the key.";
/** The newest activation `seq` this person has accepted for `orgId`, so a
 * change of key is never signed below it (a Hub restored from a backup, or
 * lying, reports an older `seq`; a key signed at it would be refused by
 * everyone who has seen the newer one, or fork the history). */
export async function hubKeyBehind(orgId, orgKey) {
    const floor = acceptedFloor(orgId, await getPins());
    if (!floor)
        return null;
    // No activation at all, for a client with one accepted, is behind too.
    if (orgKey.seq === undefined)
        return BEHIND;
    if (orgKey.seq < floor.seq || (orgKey.seq === floor.seq && floor.key !== orgKey.public_key))
        return BEHIND;
    return null;
}
/** Whether signing can be set up for `orgId` from this browser: never for
 * a client this person has already accepted an identity for, whatever the
 * Hub shows now (a Hub restored from before signing, or hiding it). A second
 * identity would be refused by everyone who pinned the first. */
export async function signingAlreadyKnown(orgId) {
    const pins = await getPins();
    if (pins.orgIdentities[orgId] || acceptedFloor(orgId, pins))
        return "You've already accepted a signing identity for this client, but the Hub doesn't show one (it may have been restored from a backup, or changed). Nothing was signed. Tell DELT4 before setting up signing again.";
    return null;
}
/** Opens, checks and unlocks every client key shared with the signed-in
 * user. Returns how many were unlocked. */
export async function restoreSharedKeys() {
    // Whose restore this is, taken before anything is awaited: the account key
    // the grants are opened with is this one, and if the person signs out or
    // someone else signs in at any point, it stops and unlocks nothing (the
    // check runs after every wait, the listing included).
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return 0;
    const privateKey = base64ToBytes(held);
    const session = sessionStill();
    const stillTheirs = () => session() && useAccountStore().privateKeyBase64 === held;
    let grants;
    try {
        grants = await hubClient.listMyKeyGrants();
    }
    catch {
        return 0;
    }
    if (!stillTheirs())
        return 0;
    let restored = 0;
    for (const grant of grants) {
        if (!stillTheirs())
            break;
        const orgId = grant.organization_id;
        let secret = null;
        try {
            secret = await openSecret(privateKey, grant.sealed_secret, {
                orgId,
                keyVersion: grant.org_key_version,
            });
        }
        catch {
            // Damaged, or sealed for something else.
        }
        let verified = null;
        const seen = {};
        let current = null;
        try {
            if (secret) {
                verified = await verifyHeldSecret(orgId, secret, seen);
            }
            // The version the Hub lists now, so only a copy at it is ever dropped:
            // the copy listed may since have been replaced by a fresh share of a
            // new key (one copy per person and client), which must stay.
            if (!verified)
                current = (await hubClient.getOrgKey(orgId))?.key_version ?? null;
        }
        catch {
            // The Hub couldn't be asked (unreachable, a proxy's 502): the copy
            // stays for next time.
        }
        if (!stillTheirs())
            break;
        if (secret && verified) {
            useOrgKeyUnlockStore().unlock(secret, orgId);
            restored += 1;
        }
        else if ((!secret || seen.otherKey || seen.outdated) &&
            current !== null &&
            grant.org_key_version === current &&
            !pinsFrozen()) {
            // A copy at the current version that this account can't open
            // (damaged, or sealed for another key), holding some other key than the
            // client's, or the client's key in the form from before signing: drop
            // it, so someone holding the key shares a good one (the Hub counts a
            // copy at the current version as the person having the key, so nobody
            // would). A copy at an older version doesn't count and is left alone.
            //
            // A copy of the very key the Hub lists, refused for its activation (an
            // older one than this person has accepted, or not the pinned client's
            // identity), is kept: deleting it gains nothing (the same key shared
            // again is refused the same way) and, after a Hub restored from a
            // backup, would lose the person their key. Nothing is dropped while a
            // rollback of the verifications is unacknowledged.
            await hubClient.deleteMyKeyGrant(orgId, grant.sealed_secret).catch(() => undefined);
        }
    }
    return restored;
}
export async function recipientTrust(userId, publicKey, signingKey = null, isEngine = false) {
    const privateKey = accountPrivateKey();
    if (privateKey && ownPublicKey(privateKey) === publicKey)
        return 'self';
    const pins = await getPins();
    const pinned = pins.users[userId];
    if (!pinned)
        return 'unverified';
    // Both keys as verified: an engine account whose signing key the Hub
    // now shows differently, or not at all, counts as changed.
    if ((pins.signing[userId] ?? null) !== (signingKey ?? null))
        return 'changed';
    // An account the Hub says is the engine's, verified here as staff: it
    // gets nothing until the person verifying says which it is.
    if (isEngine && !pins.engines[userId])
        return 'changed';
    return pinned === publicKey ? 'verified' : 'changed';
}
/** The signing key verified for an engine account, or null. */
export async function engineSigningKey(userId) {
    return (await getPins()).signing[userId] ?? null;
}
const sharing = new Map();
/**
 * Seals `secret` to everyone who may open `orgId`, lacks it, and is this
 * user or a verified colleague; the engine service's account gets only the
 * encryption key and the identity's public key (`engineShare`). Only once
 * it's known to be that client's current key (see `verifyHeldSecret`). One
 * run per client at a time.
 */
export function shareKey(orgId, secret, options = {}) {
    // One run per client per person: another person's share of the same
    // client never waits on (and ends with) the run of someone who has gone.
    const key = `${orgId}\u0000${useAccountStore().privateKeyBase64 ?? ''}\u0000${options.everyone ? 'all' : ''}`;
    const running = sharing.get(key);
    if (running)
        return running;
    const run = shareNow(orgId, secret, options.everyone ?? false).finally(() => {
        if (sharing.get(key) === run)
            sharing.delete(key);
    });
    sharing.set(key, run);
    return run;
}
async function shareNow(orgId, secret, everyone) {
    // Whose sharing this is, taken before anything is awaited: if the person
    // signs out, or someone else signs in, while it runs, it stops before
    // sealing anything (whom to trust comes from the account's own pins, so a
    // second person's verified colleagues would otherwise get the first
    // person's client key).
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return;
    const session = sessionStill();
    const stillTheirs = () => session() && useAccountStore().privateKeyBase64 === held;
    try {
        const orgKey = await verifyHeldSecret(orgId, secret);
        if (!orgKey || !stillTheirs())
            return;
        const recipients = await hubClient.listKeyRecipients(orgId);
        if (!stillTheirs())
            return;
        // The engine's account never gets the identity's signing key. Which
        // account is the engine's is what the person verifying said, saved with
        // the verification (`pins.engines`), never what the Hub says now: a Hub
        // that hides the flag and drops the grant still gets only the engine's
        // share sent again.
        const engines = (await getPins()).engines;
        if (!stillTheirs())
            return;
        // `everyone`: also those the Hub says have the key, whose copy may be
        // the form from before signing (setting up signing keeps the version,
        // so the Hub can't tell): a fresh share replaces it.
        for (const recipient of recipients.filter(r => everyone || !r.has_key)) {
            if (!stillTheirs())
                return;
            const trust = await recipientTrust(recipient.user_id, recipient.public_key, recipient.signing_public_key ?? null, !!recipient.is_engine);
            if (trust !== 'self' && trust !== 'verified')
                continue;
            if (!stillTheirs())
                return;
            const shared = engines[recipient.user_id] ? engineShare(secret) : secret;
            if (!shared)
                continue;
            try {
                const sealed = await sealSecret(recipient.public_key, shared, {
                    orgId,
                    keyVersion: orgKey.key_version,
                });
                if (!stillTheirs())
                    return;
                await hubClient.grantKey(orgId, recipient.user_id, recipient.public_key, orgKey.key_version, sealed);
            }
            catch {
                // Their key changed meanwhile, or they were unassigned: skip them.
            }
        }
    }
    catch {
        // The Hub is unreachable or the client gone; retried next time.
    }
}
/** Shares a client's key now, if this page holds it in full (after an
 * admin assigns someone to it, say). */
export function shareHeldKey(orgId) {
    const secret = useOrgKeyUnlockStore().heldSecret(orgId);
    return secret ? shareKey(orgId, secret) : Promise.resolve();
}
/** Shares every client key this page holds in full. */
export async function shareAllHeldKeys() {
    const unlock = useOrgKeyUnlockStore();
    await Promise.all(Object.keys(unlock.keys).map(orgId => shareHeldKey(orgId)));
}
/** The person verifying confirmed this colleague's key fingerprint with
 * them (or, for the engine's account, against what the engine server
 * printed): pin it, and share every held client key they should have.
 * `signingKey` is the one the Hub lists for the account, if any. */
export async function verifyPerson(userId, publicKey, isEngine = false, signingKey = null) {
    // Only the engine's account has a signing key. One verified as staff
    // would be sent whole client keys, so that's refused outright rather than
    // left to the fingerprint check.
    if (!isEngine && signingKey) {
        throw new Error("The Hub lists a signing key for this account, which only the engine's account has. If you're verifying the engine, tick the box; if this is a colleague, stop: the Hub isn't telling the truth about their account.");
    }
    if (isEngine && !signingKey) {
        throw new Error("The engine hasn't signed in yet, so its signing key isn't on the Hub: finish its setup (deploy/settings.py engine), then verify it.");
    }
    // The person verifying says whether it's the engine's account (they're
    // reading its fingerprint off the engine server, which covers its signing
    // key too), and that's saved with the verification itself, in one step,
    // before any key goes.
    await pinPerson(userId, publicKey, isEngine, signingKey);
    await shareAllHeldKeys();
}
/** The engine accounts among a client's key recipients: those this user
 * verified as the engine's, and those the Hub says are, so they can be
 * verified from the Zunox tab. Never this user's own account. */
export async function engineChoices(recipients, ownUserId) {
    const pins = await getPins();
    const out = [];
    for (const r of recipients) {
        if (r.user_id === ownUserId || !(r.is_engine || pins.engines[r.user_id]))
            continue;
        const listed = r.signing_public_key ?? null;
        const trust = await recipientTrust(r.user_id, r.public_key, listed, !!r.is_engine);
        const verified = trust === 'verified' && !!pins.engines[r.user_id];
        out.push({
            userId: r.user_id,
            name: r.display_name || r.email,
            publicKey: r.public_key,
            fingerprint: accountFingerprint(r.public_key, listed),
            verified,
            hasKey: r.has_key,
            signingKey: verified ? pins.signing[r.user_id] ?? null : null,
            listedSigningKey: listed,
        });
    }
    return out;
}
